import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useTheme } from 'next-themes';
import TagBadge from '../components/TagBadge';
import { ArrowLeft, Calendar, Clock, Copy, Check } from 'lucide-react';
import { getPostBySlug } from '../data/posts';

function CopyButton({ code }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={copy}
      data-testid="code-copy-btn"
      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

function CodeBlock({ children, className }) {
  const { resolvedTheme } = useTheme();
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : '';
  const code = String(children).replace(/\n$/, '');

  if (!language) {
    return (
      <code className="bg-muted text-foreground px-1.5 py-0.5 rounded text-sm font-mono">
        {children}
      </code>
    );
  }

  return (
    <div className="relative my-6 rounded-lg overflow-hidden border border-border">
      <div className="flex items-center justify-between px-4 py-2 bg-muted border-b border-border">
        <span className="text-xs font-mono text-muted-foreground">{language}</span>
        <CopyButton code={code} />
      </div>
      <SyntaxHighlighter
        style={resolvedTheme === 'dark' ? oneDark : oneLight}
        language={language}
        PreTag="div"
        customStyle={{
          margin: 0,
          borderRadius: 0,
          border: 'none',
          fontSize: '0.875rem',
          lineHeight: '1.6',
        }}
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
}

export default function PostDetail() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <p className="text-muted-foreground text-lg">Post not found</p>
        <Link to="/" className="mt-6 inline-block text-sm underline underline-offset-4 hover:text-primary transition-colors">
          Go back home
        </Link>
      </div>
    );
  }

  return (
    <article className="max-w-3xl mx-auto px-6 md:px-0 py-12 animate-in" data-testid="post-detail">
      {/* Back */}
      <Link
        to="/"
        data-testid="back-to-posts"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-10"
      >
        <ArrowLeft className="h-4 w-4" /> Back to posts
      </Link>

      {/* Meta */}
      <header className="mb-8">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <TagBadge tag={post.category} size="md" />
          {post.tags?.filter(t => t !== post.category).map(t => (
            <TagBadge key={t} tag={t} />
          ))}
        </div>
        <h1
          className="text-4xl md:text-5xl font-bold tracking-tight leading-tight mb-5"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          {post.title}
        </h1>
        {post.excerpt && (
          <p className="text-lg text-muted-foreground mb-5">{post.excerpt}</p>
        )}
        <div className="flex items-center gap-6 text-sm text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <Calendar className="h-4 w-4" />
            {new Date(post.created_at).toLocaleDateString('en-US', {
              year: 'numeric', month: 'long', day: 'numeric',
            })}
          </span>
          <span className="flex items-center gap-1.5">
            <Clock className="h-4 w-4" />
            {post.reading_time} min read
          </span>
        </div>
      </header>

      {/* Cover image */}
      {post.cover_image && (
        <div className="mb-10 rounded-lg overflow-hidden border border-border">
          <img
            src={post.cover_image}
            alt={post.title}
            className="w-full object-cover max-h-[420px]"
          />
        </div>
      )}

      {/* Markdown content */}
      <div className="prose">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{ code: CodeBlock }}
        >
          {post.content}
        </ReactMarkdown>
      </div>

      {/* Footer nav */}
      <div className="mt-14 pt-8 border-t border-border">
        <Link
          to="/"
          data-testid="post-footer-back"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Back to all posts
        </Link>
      </div>
    </article>
  );
}
